import { useEffect, useState } from "react"
import { getBmdTopicsForYear } from "../services/bmd.service"
import type { BmdTopic } from "../types/bmd.types"

export type UseBmdTopicsState = {
  topics: BmdTopic[]
  lockedTopicIds: Set<number>
  loading: boolean
  error: string | null
}

export function useBmdTopics(
  yearLevelId: number,
  subjectId: number,
  studentKey?: string,
): UseBmdTopicsState {
  const [topics, setTopics] = useState<BmdTopic[]>([])
  const [lockedTopicIds, setLockedTopicIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!yearLevelId || !subjectId) {
      setTopics([])
      setLockedTopicIds(new Set())
      setLoading(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    getBmdTopicsForYear(
      {
        year_level_id: yearLevelId,
        subject_id: subjectId,
        student_key: studentKey,
      },
      controller.signal,
    )
      .then((res) => {
        setTopics(res.topics)
        setLockedTopicIds(new Set(res.locked_topic_ids || []))
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load diagnostics topics")
        setTopics([])
        setLockedTopicIds(new Set())
        setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [yearLevelId, subjectId, studentKey])

  return { topics, lockedTopicIds, loading, error }
}
